import { useCallback, useEffect, useRef, useState } from 'react';
import ListSubheader from '@mui/material/ListSubheader';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Typography } from '@mui/material';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import styles from './../../common/aur-select-dropdown/style.module.scss';
import { t } from 'i18next';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import TaggingPanel from './tagging-panel/TaggingPanel';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import HighlightPanel from './highlight-panel/EcaHighLightedPanel';

type ListItemType = {
  name: string;
  icon: React.ReactNode;
  endIcon?: React.ReactNode;
  nestedItems?: ListItemType[];
};

interface NestedListProps {
  title?: string;
  items: ListItemType[];
  activeDocs: string;
  handleClickDocument: (docs: string) => void;
  handleScroll: (event: React.UIEvent<HTMLElement>) => void;
  documentTopLoading: boolean;
  documentBottomLoading: boolean;
  searchId: number;
  indexName: string;
  stdIndexFieldTemplateId?: number;
  openLeftPanel: () => void;
}

const DOC_LIST_HEIGHT = 224;
const DOC_ITEM_SIZE = 32;
const DOCUMENTS_SECTION_INDEX = 0;

type DocumentRowData = {
  items: ListItemType[];
  activeDocs: string;
  leftPanelResize: boolean;
  onClickDocument: (name: string) => void;
};

const NestedList: React.FC<NestedListProps> = ({
  title,
  items,
  activeDocs,
  handleClickDocument,
  handleScroll,
  documentTopLoading,
  documentBottomLoading,
  searchId,
  indexName,
  stdIndexFieldTemplateId,
  openLeftPanel,
}) => {
  const [openIndex, setOpenIndex] = useState<number[]>([]);
  const SCROLL_BAR_STYLE_CLASS = 'aur-item-container-scroll';
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const docsListRef = useRef<FixedSizeList>(null);
  const docsListOuterRef = useRef<HTMLDivElement>(null);

  const leftPanelResize = useSelector(
    (state: RootState) => state.ecaDocumentViewer.leftPanel
  );

  const showHighLightedTab = useSelector(
    (state: RootState) => state.ecaDocumentViewer.showHighLightedTab
  );

  const documentsNestedItems =
    items[DOCUMENTS_SECTION_INDEX]?.nestedItems;

  useEffect(() => {
    if (leftPanelResize === true) setOpenIndex([]);
  }, [leftPanelResize]);

  // Toggle the nested list on click
  const handleClick = (index: number) => {
    setActiveIndex(index);
    setOpenIndex((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        if (leftPanelResize) {
          openLeftPanel();
        }
        return [...prev, index];
      }
    });
  };

  const scrollToActiveDocument = useCallback(() => {
    if (!documentsNestedItems) {
      return;
    }
    const index = documentsNestedItems.findIndex(
      (doc) => doc.name === activeDocs
    );
    if (index >= 0) {
      docsListRef.current?.scrollToItem(index, 'smart');
    }
  }, [activeDocs, openIndex]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (activeIndex === DOCUMENTS_SECTION_INDEX) {
        scrollToActiveDocument();
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [openIndex, activeIndex, scrollToActiveDocument]);

  useEffect(() => {
    if (openIndex?.includes(DOCUMENTS_SECTION_INDEX)) {
      scrollToActiveDocument();
    }
  }, [activeDocs, openIndex, scrollToActiveDocument]);

  const handleDocsListScroll = useCallback(() => {
    if (docsListOuterRef.current) {
      handleScroll({
        target: docsListOuterRef.current,
      } as unknown as React.UIEvent<HTMLElement>);
    }
  }, [handleScroll]);

  const DocumentRow = useCallback(
    ({ index, style, data }: ListChildComponentProps<DocumentRowData>) => {
      const item = data.items[index];
      if (!item) {
        return null;
      }

      return (
        <div style={style} className="list-item-container">
          <ListItemButton
            style={{ padding: '8px' }}
            onClick={() => data.onClickDocument(item.name)}
            className={`${data.activeDocs === item.name ? 'document-active' : ''} collapse-doc-list`}
          >
            <ListItemIcon
              className={data.leftPanelResize ? '' : 'doc-list-icon'}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              className="typo-text body2_size mist-100"
              primary={item.name}
            />
            <span className="highlight-on"> {item.endIcon}</span>
          </ListItemButton>
        </div>
      );
    },
    []
  );

  const renderComponentByType = (
    componentType: string,
    nestedItems: ListItemType[],
    index: number,
    isExpanded: boolean
  ) => {
    switch (componentType) {
      // case t(`eca.audit.audit_log`):
      //   return <EcaDocumentAuditLog data-testid="expanded-content" />;
      case t(`eca.documentPreviewer.taggingPanel.tagging`):
        return (
          <TaggingPanel
            activeDocumentId={activeDocs}
            searchId={searchId}
            indexName={indexName}
            stdIndexFieldTemplateId={stdIndexFieldTemplateId}
            isExpanded={isExpanded}
          />
        );
      // case t(`eca.documentPreviewer.highlightsPanel.highlights`):
      //   return <HighlightPanel  />;
      case t('eca.documentPreviewer.highlightsPanel.highlights'):
        return showHighLightedTab ? <HighlightPanel /> : null;
      default:
        return (
          <List component="div" disablePadding sx={{ pl: 4 }}>
            {renderListItems(nestedItems, index)}
          </List>
        );
    }
  };

  const renderDocumentsList = (nestedItems: ListItemType[]) => {
    return (
      <div>
        {documentTopLoading && (
          <div className="doc-loading-item">
            <AurLoadingIndicator size={24} />
            <Typography className="typo-text body4_size">
              {t('eca.documentPreviewer.loading')}
            </Typography>
          </div>
        )}
        <FixedSizeList
          height={DOC_LIST_HEIGHT}
          width="100%"
          itemCount={nestedItems.length}
          itemSize={DOC_ITEM_SIZE}
          itemData={{
            items: nestedItems,
            activeDocs,
            leftPanelResize,
            onClickDocument: handleClickDocument,
          }}
          className={`${SCROLL_BAR_STYLE_CLASS} ${styles.aurItemContainerScrollbar} doc-sub-list`}
          outerRef={docsListOuterRef}
          onScroll={handleDocsListScroll}
          ref={docsListRef}
        >
          {DocumentRow}
        </FixedSizeList>
        {documentBottomLoading && (
          <div className="doc-loading-item">
            <AurLoadingIndicator size={24} />
            <Typography className="typo-text body4_size">
              {t('eca.documentPreviewer.loading')}
            </Typography>
          </div>
        )}
      </div>
    );
  };

  // Render top-level accordion list items
  const renderListItems = (listItems: ListItemType[], parentIndex = -1) => {
    return listItems.map((item, index) => {
      const isNestedOpen = openIndex?.includes(index);
      return (
        <div
          key={`${item.name}-${index}`}
          style={{
            borderBottom: parentIndex !== 0 ? '1px solid #2D3D55' : 'none',
          }}
        >
          <div
            key={`${item.name}-${index}`}
            className={
              item.nestedItems && parentIndex !== 0 ? '' : 'list-item-container'
            }
          >
            <ListItemButton
              style={{ padding: '8px' }}
              onClick={() =>
                item.nestedItems
                  ? handleClick(index)
                  : handleClickDocument(item.name)
              }
              className={` ${activeDocs === item.name && 'document-active'} ${item.nestedItems ? 'parent-list' : 'collapse-doc-list'}`}
            >
              <ListItemIcon className={leftPanelResize ? '' : 'doc-list-icon'}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                className={`typo-text ${item.nestedItems ? 'subhead3_size' : 'body2_size'} mist-100`}
                primary={item.name}
              />
              <span className="highlight-on"> {item.endIcon}</span>
              {item.nestedItems &&
                (isNestedOpen ? <ExpandLess /> : <ExpandMore />)}{' '}
            </ListItemButton>
            {item.nestedItems && (
              <Collapse
                in={isNestedOpen}
                timeout="auto"
                unmountOnExit
                className={item.name === 'Documents' ? `collapse-list` : ''}
              >
                {item.name === 'Documents' && (
                  <Typography className="typo-text subhead4_size doc-sub-label">
                    DocumentID
                  </Typography>
                )}
                {item.name === 'Documents'
                  ? renderDocumentsList(item.nestedItems)
                  : renderComponentByType(
                      item.name,
                      item.nestedItems,
                      index,
                      isNestedOpen
                    )}
              </Collapse>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <List
      sx={{ width: '100%', minWidth: 440, bgcolor: 'background.paper' }}
      component="nav"
      aria-labelledby="nested-list-subheader"
      aria-label={t('eca.documentPreviewer.nestedListSubheader')}
      subheader={
        <ListSubheader component="div" id="nested-list-subheader">
          {title}
        </ListSubheader>
      }
    >
      {renderListItems(items)}
    </List>
  );
};

export default NestedList;
